import React, { useState, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  InputGroup,
  InputGroupText,
  Spinner,
} from "reactstrap";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../../../constants/AuthContext";
import { toast } from "react-toastify";

import "../../../../assets/FrontEnd/css/font.css";
import "../../../../assets/FrontEnd/plugins/bootstrap/css/bootstrap.min.css";
import "../../../../assets/FrontEnd/css/style.css";

const FrontPageRegister = () => {
  const navigate = useNavigate();
  const { setAuthUser } = useContext(AuthContext);

  // states
  const [email, setEmail] = useState("");
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [phone_code, setPhoneCode] = useState("+92");
  const [mobno, setMobNo] = useState("");
  const [role, setRole] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // validation touched state
  const [touched, setTouched] = useState({
    first_name: false,
    last_name: false,
    email: false,
    password: false,
    mobno: false,
    role: false,
  });

  // validation rules
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  const isEmailValid = emailRegex.test(email.trim());
  const isPasswordValid = password.trim().length >= 6;
  const isFirstNameValid = first_name.trim() !== "";
  const isLastNameValid = last_name.trim() !== "";
  const isMobValid = mobno.trim().length >= 7;
  const isRoleValid = role !== "";

  const formValid =
    isEmailValid &&
    isPasswordValid &&
    isFirstNameValid &&
    isLastNameValid &&
    isMobValid &&
    isRoleValid;

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      password: true,
      mobno: true,
      role: true,
    });

    if (!formValid) return;

    setSubmitting(true);
    setServerError("");
    setFieldErrors({});

    try {
      let formData = new FormData();
      formData.append("first_name", first_name);
      formData.append("last_name", last_name);
      formData.append("emailid", email.trim());
      formData.append("password", password);
      formData.append("phone_code", phone_code);
      formData.append("mobno", mobno);
      formData.append("user_type", role);

      const response = await axios.post(
        process.env.REACT_APP_WEB_APP_URL + "/api/user-registration",
        formData
      );

      if (response.status !== 200) {
        toast.error(response?.data?.message || "Registration failed. Check credentials.");
        setSubmitting(false);
        return;
      }

      setAuthUser(response.data?.data?.userinfo);
      localStorage.setItem("authToken", response.data?.data?.userinfo?.api_token);
      localStorage.setItem("isProfileLocked", false);
      localStorage.setItem("authUserPassword", password);

      navigate("/");
    } catch (err) {
      if (err.response?.status === 422) {
        const errors = err.response?.data?.errors || {};
        Object.keys(errors).forEach((field) => {
          errors[field].forEach((message) => {
            toast.error(message);
          });
        });
      } else {
        toast.error(
          err?.response?.data?.error || "Network error. Please try again."
        );
      }
      setSubmitting(false);
    }
  };

  document.title = "Register | Sawari Online";

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col xs="12" sm="10" md="8" lg="6" xl="5">
          <Card className="shadow-sm">
            <CardBody className="p-4">
              <h3 className="mb-3 text-center">Welcome To Sawari Online</h3>
              <p className="text-center text-muted mb-4">
                Sign up to continue to your account
              </p>

              {serverError && (
                <div className="alert alert-danger py-2">{serverError}</div>
              )}

              <Form onSubmit={handleSubmit} noValidate>
                {/* First + Last Name */}
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        value={first_name}
                        onChange={(e) => setFirstName(e.target.value)}
                        onBlur={() =>
                          setTouched((t) => ({ ...t, first_name: true }))
                        }
                        invalid={touched.first_name && !isFirstNameValid}
                      />
                      <FormFeedback>Please enter a valid First Name.</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={last_name}
                        onChange={(e) => setLastName(e.target.value)}
                        onBlur={() =>
                          setTouched((t) => ({ ...t, last_name: true }))
                        }
                        invalid={touched.last_name && !isLastNameValid}
                      />
                      <FormFeedback>Please enter a valid Last Name.</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Email */}
                <FormGroup>
                  <Label for="email">Email</Label>
                  <InputGroup>
                    <InputGroupText>
                      <FaEnvelope />
                    </InputGroupText>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, email: true }))
                      }
                      invalid={touched.email && !isEmailValid}
                    />
                    <FormFeedback>Please enter a valid email.</FormFeedback>
                  </InputGroup>
                </FormGroup>

                {/* Password */}
                <FormGroup>
                  <Label for="password">Password</Label>
                  <InputGroup>
                    <InputGroupText>
                      <FaLock />
                    </InputGroupText>
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, password: true }))
                      }
                      invalid={touched.password && !isPasswordValid}
                    />
                    <InputGroupText
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </InputGroupText>
                    <FormFeedback>
                      Password must be at least 6 characters.
                    </FormFeedback>
                  </InputGroup>
                </FormGroup>

                {/* Phone Code + Mobile */}
                <FormGroup>
                  <Label>Mobile Number</Label>
                  <InputGroup>
                    <Input
                      type="select"
                      value={phone_code}
                      onChange={(e) => setPhoneCode(e.target.value)}
                      style={{ maxWidth: "120px" }}
                    >
                      <option value="+92">🇵🇰 +92</option>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                    </Input>
                    <Input
                      type="text"
                      placeholder="3001234567"
                      value={mobno}
                      onChange={(e) => setMobNo(e.target.value)}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, mobno: true }))
                      }
                      invalid={touched.mobno && !isMobValid}
                    />
                    <FormFeedback>Please enter a valid mobile number.</FormFeedback>
                  </InputGroup>
                </FormGroup>

                {/* Role */}
                <FormGroup>
                  <Label for="role">Role</Label>
                  <Input
                    id="role"
                    type="select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, role: true }))}
                    invalid={touched.role && !isRoleValid}
                  >
                    <option value="">Select Role</option>
                    <option value="C">Customer</option>
                    <option value="D">Driver</option>
                  </Input>
                  <FormFeedback>Please select a role.</FormFeedback>
                </FormGroup>

                {/* Submit */}
                <div className="d-grid mb-3">
                  <Button color="primary" type="submit" disabled={!formValid || submitting}>
                    {submitting ? (
                      <>
                        <Spinner size="sm" /> &nbsp; Signing up...
                      </>
                    ) : (
                      "Sign Up"
                    )}
                  </Button>
                </div>

                {/* Divider */}
                <div className="text-center text-muted mb-2">or</div>

                {/* Social / Signup */}
                <div className="d-grid gap-2">
                  <Button color="link" onClick={() => navigate("/login")}>
                    Already have an account?
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FrontPageRegister;
